/**
 * Shared utilities for the Quote Builder.
 *
 * Keep this file dependency-free — it's imported by everything else
 * (hooks, layout primitives, line-item tables, the main page).
 */

// Show blank when value is 0/null/undefined so estimators can fully clear & retype.
export const numVal = (v) =>
  v === 0 || v == null || Number.isNaN(v) ? "" : v;

export const parseNum = (s) => (s === "" || s == null ? 0 : Number(s));

// Reorder a list by moving `from` -> `to`. Returns a new array.
export function reorder(list, from, to) {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

// "John Doe" -> "JD" ; "Sarah" -> "SA" ; fallback to email prefix
export function initialsOf(user) {
  const name = (user?.name || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return ((user?.email || "?").split("@")[0] || "?").slice(0, 2).toUpperCase();
}

// Compact UK-style date: "14 Feb 26 14:32"
export function fmtAuditDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${yy} ${hh}:${mm}`;
}

// Order branches as labour types: Plymouth Basic Rate, Bristol Basic Rate, then alphabetical.
export function sortLabourTypes(branches) {
  const list = [...branches];
  const pri = ["Plymouth Basic Rate", "Bristol Basic Rate"];
  list.sort((a, b) => {
    const ai = pri.indexOf(a.name);
    const bi = pri.indexOf(b.name);
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
    return a.name.localeCompare(b.name);
  });
  return list;
}

// Monotonic id factory for transient list keys
let _tmpId = 0;
export const tmpId = () => `tmp-${++_tmpId}-${Date.now()}`;

// Shared dropdown option lists
// Note: "Access Hire" was removed from materials categories on 2026-06-04 in favour
// of a dedicated Access Hire section on the Quote Builder.
export const CATEGORIES = ["Parts", "Powder Coating", "Transport", "Plant Hire", "Other"];
export const DIFFICULTIES = ["Standard", "Complex"];
export const JOB_TYPES = ["Repair", "Install"];
